/* Canspeed storefront behaviour: homepage showcase switching + product variant switching.
   Progressive enhancement only — every control works without JS. */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     Homepage product showcase
     The .package-card buttons previously had hover/selected styling but no
     behaviour. They now swap the large showcase image and retarget the
     "See Detail" links at the matching variant.
     --------------------------------------------------------------------- */
  function initShowcase(root) {
    (root || document).querySelectorAll('[data-product-showcase]').forEach(function (showcase) {
      if (showcase.dataset.showcaseReady === 'true') return;

      var cards = showcase.querySelectorAll('.package-card');
      var frame = showcase.querySelector('[data-showcase-frame]');
      var image = frame && frame.querySelector('img');
      if (cards.length === 0 || !image) return;

      showcase.dataset.showcaseReady = 'true';
      var links = showcase.querySelectorAll('[data-showcase-link]');

      cards.forEach(function (card) {
        card.addEventListener('click', function () {
          var src = card.getAttribute('data-product-image');
          var label = card.getAttribute('data-product-label');
          var url = card.getAttribute('data-product-url');
          var color = card.getAttribute('data-size-color');

          cards.forEach(function (other) {
            other.classList.toggle('is-selected', other === card);
            other.setAttribute('aria-pressed', other === card ? 'true' : 'false');
          });

          if (src) {
            image.removeAttribute('srcset');
            image.src = src;
          }
          if (label) image.alt = label;

          if (frame && color) {
            frame.classList.remove('is-size-green', 'is-size-purple', 'is-size-orange');
            frame.classList.add('is-size-' + color);
          }

          if (url) {
            links.forEach(function (link) { link.href = url; });
          }
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Product page variant switching
     Reads the variant JSON emitted by the product sections and keeps price,
     availability, main image and the ?variant= URL in sync with the select.
     --------------------------------------------------------------------- */
  function initVariantPickers(root) {
    (root || document).querySelectorAll('[data-variant-root]').forEach(function (section) {
      if (section.dataset.variantReady === 'true') return;

      var select = section.querySelector('[data-variant-select]');
      var dataEl = section.querySelector('[data-variant-data]');
      if (!select || !dataEl) return;

      var variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (error) {
        return;
      }
      if (!Array.isArray(variants) || variants.length === 0) return;

      section.dataset.variantReady = 'true';

      var priceEl = section.querySelector('[data-variant-price]');
      var comparePriceEl = section.querySelector('[data-variant-compare-price]');
      var submitEl = section.querySelector('[data-variant-submit]');
      var imageFrame = section.querySelector('[data-variant-image-frame]');
      var mainImage = imageFrame && imageFrame.querySelector('img');
      var soldOutLabel = section.getAttribute('data-sold-out-label') || 'Sold out';
      var addLabel = section.getAttribute('data-add-label') || 'Add to cart';

      function apply(variantId) {
        var variant = variants.filter(function (candidate) {
          return String(candidate.id) === String(variantId);
        })[0];
        if (!variant) return;

        if (priceEl) priceEl.textContent = variant.price;

        if (comparePriceEl) {
          if (variant.compare_at_price) {
            comparePriceEl.textContent = variant.compare_at_price;
            comparePriceEl.hidden = false;
          } else {
            comparePriceEl.hidden = true;
          }
        }

        if (submitEl) {
          submitEl.disabled = !variant.available;
          submitEl.textContent = variant.available ? addLabel : soldOutLabel;
        }

        if (mainImage && variant.image) {
          mainImage.removeAttribute('srcset');
          mainImage.src = variant.image;
          mainImage.alt = variant.title;
        }

        if (window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      select.addEventListener('change', function () {
        apply(select.value);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Form feedback
     Contact forms live inside [data-reveal] blocks, which GSAP starts at
     opacity 0 and only animates in on scroll. After a submit the page can
     land somewhere that never triggers that reveal, leaving the confirmation
     invisible — the buyer has no idea whether the enquiry was sent. Force the
     surrounding block visible, scroll to it, and move focus onto it.
     --------------------------------------------------------------------- */
  function initFormFeedback(root) {
    var message = (root || document).querySelector('.form-success, .form-errors');
    if (!message || message.dataset.feedbackHandled === 'true') return;
    message.dataset.feedbackHandled = 'true';

    var node = message;
    while (node && node !== document.body) {
      if (node.hasAttribute && node.hasAttribute('data-reveal')) {
        node.classList.add('reveal-force-visible');
      }
      node = node.parentElement;
    }

    /* This page is image-heavy and the message sits near the bottom, so a
       single scroll on DOMContentLoaded lands in the wrong place: images that
       decode afterwards push the message thousands of pixels further down.
       Re-run the scroll as the layout settles — on each late image, on window
       load, and once more after that. `behavior: auto` overrides the global
       `scroll-behavior: smooth`, which would otherwise animate every attempt
       and let them fight each other. */
    var scrollToMessage = function () {
      message.scrollIntoView({ block: 'center', behavior: 'auto' });
    };

    window.requestAnimationFrame(function () {
      scrollToMessage();
      if (typeof message.focus === 'function') message.focus({ preventScroll: true });
    });

    Array.prototype.forEach.call(document.images, function (image) {
      if (!image.complete) {
        image.addEventListener('load', scrollToMessage, { once: true });
        image.addEventListener('error', scrollToMessage, { once: true });
      }
    });

    if (document.readyState === 'complete') {
      window.setTimeout(scrollToMessage, 200);
    } else {
      window.addEventListener('load', function () {
        scrollToMessage();
        window.setTimeout(scrollToMessage, 200);
      }, { once: true });
    }
  }

  function init(root) {
    initShowcase(root);
    initVariantPickers(root);
    initFormFeedback(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
